import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import StorageIcon from '@mui/icons-material/Storage';
import SecurityIcon from '@mui/icons-material/Security';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

const InfoCard = () => {
  const theme = useTheme();

  return (
    <StyledCard>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          About File Upload
        </Typography>

        <Typography variant="body1" paragraph>
          This application allows you to upload files to Vercel Blob Storage with Airtable metadata tracking,
          providing secure, scalable cloud storage with SHA-256 file verification and global CDN delivery.
        </Typography>
        
        <Divider sx={{ my: 2 }} />
        
        {/* How It Works */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoIcon sx={{ mr: 1, color: theme.palette.info.main }} />
            How It Works
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemText
                primary="Cloud Storage"
                secondary="Files are stored in Vercel's global infrastructure with automatic redundancy."
                primaryTypographyProps={{ fontWeight: 'medium' }}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="SHA-256 Verification"
                secondary="Each file is verified with SHA-256 hashing to ensure integrity and authenticity."
                primaryTypographyProps={{ fontWeight: 'medium' }}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="CDN Delivery"
                secondary="Files are served via global CDN for fast access from anywhere in the world."
                primaryTypographyProps={{ fontWeight: 'medium' }}
              />
            </ListItem>
          </List>
        </Box>
        
        {/* Important Notes */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon sx={{ mr: 1, color: theme.palette.warning.main }} />
            Important Notes
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemText
                primary="Access Control"
                secondary="Files can be configured with different access levels (Public, Authenticated, Private, Admin Only)."
                primaryTypographyProps={{ fontWeight: 'medium', color: theme.palette.warning.main }}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Airtable Metadata"
                secondary="File metadata and access information are stored in Airtable for complete tracking and management."
                primaryTypographyProps={{ fontWeight: 'medium' }}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="File Size Limits"
                secondary="This app limits uploads to 100MB per file to ensure optimal performance."
                primaryTypographyProps={{ fontWeight: 'medium' }}
              />
            </ListItem>
          </List>
        </Box>
        
        {/* Technical Details */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <StorageIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            Technical Details
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemText
                primary="Vercel Platform"
                secondary="This app uses Vercel Blob Storage for scalable, secure file storage with automatic CDN integration."
                primaryTypographyProps={{ fontWeight: 'medium' }}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="SHA-256 Hashing"
                secondary="Files are content-addressed using SHA-256 hashes (64-character hexadecimal) for verification and integrity."
                primaryTypographyProps={{ fontWeight: 'medium' }}
              />
            </ListItem>
          </List>
        </Box>
        
        {/* Security Recommendations */}
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ mr: 1, color: theme.palette.secondary.main }} />
            Security Recommendations
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemText
                primary="Access Levels"
                secondary="Configure appropriate access levels for files based on sensitivity (Public, Authenticated, Private, Admin Only)."
                primaryTypographyProps={{ fontWeight: 'medium' }}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="HTTPS Delivery"
                secondary="All files are delivered over HTTPS for secure transmission and protection against tampering."
                primaryTypographyProps={{ fontWeight: 'medium' }}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="File Integrity"
                secondary="SHA-256 hashes allow verification that files haven't been modified or corrupted."
                primaryTypographyProps={{ fontWeight: 'medium' }}
              />
            </ListItem>
          </List>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default InfoCard;